import classnames from 'classnames';
import React, { FC, MouseEvent, useEffect, useRef } from 'react';
import { symbol as Symbol } from '@wix/santa-editor-symbols';
import { ContextMenu, ContextMenuAction, Symbol as UISymbol, TextLabel } from '@wix/wix-base-ui';
import { useSavedComponentActionsDispatcher } from '../../../../../../../ActionsDispatcher';
import type { DragItemRect } from '../../../../../../../ActionsDispatcher/ActionsDispatcher.types';
import { useCurrentCategory } from '../../../../../../../CurrentCategoryProvider';
import { useBoolean } from '../../../../../../../hooks/useBoolean';
import { usePreventDoubleClick } from '../../../../../../../hooks/usePreventDoubleClick';
import { useUtils } from '../../../../../../../UtilsProvider';
import { SafeInjectHtml } from '../../../../../../../utils/safeInjectHtml/safeInjectHtml';
import styles from './SnapshotItem.scss';
import type { ContextMenuItem, SnapshotItemProps } from './SnapshotItem.types';

const PREVIEW_WIDTH = 234;
const PREVIEW_HEIGHT_LANDSCAPE = 90;
const PREVIEW_HEIGHT_PORTRAIT = 144;
const LANDSCAPE_RATIO = 1.5;

const getPreviewDimensions = (element: Element): DragItemRect => {
  const { left, top, width, height } = element.getBoundingClientRect();

  return { left, top, width, height };
};

const isIntersectionObserverSupported = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.IntersectionObserver);

export const SnapshotItem: FC<SnapshotItemProps> = ({ item, section }) => {
  const {
    id,
    name,
    previewHTML,
    isOptimistic = false,
    previewDimension: { width, height },
  } = item;
  const currentCategory = useCurrentCategory();
  const isLandscape = width / height > LANDSCAPE_RATIO;
  const previewHeight = isLandscape ? PREVIEW_HEIGHT_LANDSCAPE : PREVIEW_HEIGHT_PORTRAIT;
  const scale = Math.min(1, PREVIEW_WIDTH / width, previewHeight / height);
  const { value: isContextMenuOpen, set: setContextMenuOpen } = useBoolean(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  const { translate } = useUtils();
  const { isAnotherMouseActionAllowed, setClickTimeStamp } = usePreventDoubleClick();
  const {
    dispatchSavedComponentAppeared,
    dispatchAddComponent,
    dispatchRenameItem,
    dispatchAddComponentAndApplyTheme,
    dispatchDeleteItem,
    dispatchStartComponentDrag,
    dispatchComponentClick,
  } = useSavedComponentActionsDispatcher();

  const handleClick = (event: MouseEvent) => {
    if (isAnotherMouseActionAllowed(event)) {
      dispatchComponentClick({ id, applyTheme: false });
    }

    setClickTimeStamp(event);
  };

  const handleMouseDown = (event: MouseEvent) => {
    event.preventDefault();
    event.persist();

    if (!previewRef.current || !isAnotherMouseActionAllowed(event)) {
      return;
    }

    const rect = getPreviewDimensions(previewRef.current);

    dispatchStartComponentDrag({
      event,
      sectionName: section.sectionName,
      sectionTitle: section?.translations?.title,
      categoryId: currentCategory?.id ?? '',
      dragItemData: {
        item: {
          itemId: id,
          structure: {
            layout: {
              width,
              height,
            },
          },
        },
        rect,
      },
    });
  };

  const menuItems: ContextMenuItem[] = [
    {
      id: 'snapshot-section-item-context-menu-add',
      icon: 'addAction',
      name: 'add_panel_MyDesigns_design_more_add_tooltip',
      onClick: () => dispatchAddComponent({ id, applyTheme: false }),
    },
    {
      id: 'snapshot-section-item-context-menu-addAndApplyTheme',
      icon: 'applyTo',
      name: 'add_panel_MyDesigns_design_more_add_site_theme_tooltip',
      onClick: () => dispatchAddComponentAndApplyTheme({ id, applyTheme: true }),
    },
    {
      id: 'snapshot-section-item-context-menu-rename',
      icon: 'renameAction',
      name: 'add_panel_MyDesigns_design_more_rename_tooltip',
      onClick: () => dispatchRenameItem({ id, name }),
    },
    {
      id: 'snapshot-section-item-context-menu-delete',
      icon: 'deleteAction',
      name: 'add_panel_MyDesigns_design_more_delete_tooltip',
      onClick: () => dispatchDeleteItem({ id, name }),
    },
  ];

  const handleContextMenuToggle = (isOpen: boolean) => {
    setContextMenuOpen(isOpen);
  };

  useEffect(() => {
    if (previewHTML) {
      return;
    }

    const onViewed = () => {
      dispatchSavedComponentAppeared({ id });
    };

    const disconnect = (observer: IntersectionObserver | null) => {
      if (observer !== null) {
        observer.disconnect();
      }
    };

    if (isIntersectionObserverSupported()) {
      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            disconnect(observerRef.current);
            onViewed();
          }
        },
        {
          threshold: 0,
        },
      );

      if (previewRef.current) {
        observer.observe(previewRef.current);
      }

      observerRef.current = observer;

      return () => {
        disconnect(observerRef.current);
      };
    }

    onViewed();
  }, [id, previewHTML, dispatchSavedComponentAppeared]);

  return (
    <div data-hook={`snapshot-section-item-wrapper-${name}`} className={styles.item}>
      <TextLabel
        value={name}
        className={styles.itemName}
        dataHook="snapshot_section_item-name"
        enableEllipsis={true}
        shouldTranslate={false}
        type="T02"
      />

      <div
        className={classnames(styles.previewWrapper, {
          [styles.landscape]: isLandscape,
          [styles.active]: isContextMenuOpen,
        })}
        data-hook="snapshot-section-item-preview-wrapper"
      >
        <div
          className={styles.preview}
          ref={previewRef}
          data-hook={`snapshot-item-preview-${name}`}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
        >
          {previewHTML ? (
            <div className={styles.previewInner}>
              <SafeInjectHtml
                tag="div"
                className={styles.snapshot}
                data-hook="snapshot-item-preview-inner"
                style={{
                  transform: `translate(-50%, -50%) scale(${scale},${scale})`,
                }}
                html={previewHTML}
              />
            </div>
          ) : null}
          <div className={styles.overlay} />
        </div>

        <ContextMenu
          className={classnames(styles.contextMenu, 'flat')}
          optionsContainerClassName={styles.contextMenuOptionsContainer}
          disabled={isOptimistic}
          onToggle={handleContextMenuToggle}
          dataHook="snapshot-section-item-context-menu"
        >
          {menuItems.map((menuItem) => (
            <ContextMenuAction dataHook={menuItem.id} onClick={menuItem.onClick} key={menuItem.id}>
              <UISymbol>
                <Symbol name={menuItem.icon} />
              </UISymbol>
              <span>{translate(menuItem.name)}</span>
            </ContextMenuAction>
          ))}
        </ContextMenu>
      </div>
    </div>
  );
};
